// Shuffle state: filter the tagged collection by mood, genre and decade, then deal random records
import { ref, computed } from 'vue';
import useCollection from '@/composables/useCollection';

// Filter groups: within a group any selected value matches (OR); across groups all must match (AND)
const GROUPS = [
  { key: 'moods', label: 'Mood', values: r => r.moods },
  { key: 'genres', label: 'Genre', values: r => r.genres },
  { key: 'decades', label: 'Decade', values: r => (r.decade ? [`${r.decade}s`] : []) },
];

const shuffle = list => {
  const copy = [...list];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

export default function useShuffle() {
  const {
    releases, isLoading, error, loadCollection,
  } = useCollection();
  const selected = ref({ moods: [], genres: [], decades: [] });
  const dealt = ref([]);

  const matchesGroup = (record, group) => {
    const picks = selected.value[group.key];
    return !picks.length || group.values(record).some(v => picks.includes(v));
  };

  const matchesAll = (record, except = null) => GROUPS
    .filter(group => group.key !== except)
    .every(group => matchesGroup(record, group));

  const pool = computed(() => releases.value.filter(r => matchesAll(r)));

  const totals = computed(() => Object.fromEntries(GROUPS.map(group => {
    const counts = {};
    releases.value.forEach(r => group.values(r).forEach(v => { counts[v] = (counts[v] || 0) + 1; }));
    const values = Object.keys(counts);
    values.sort(group.key === 'decades' ? undefined : (a, b) => counts[b] - counts[a]);
    return [group.key, values];
  })));

  // Chips per group, each counting matches given the other groups' selections
  const options = computed(() => GROUPS.map(group => {
    const counts = {};
    releases.value
      .filter(r => matchesAll(r, group.key))
      .forEach(r => group.values(r).forEach(v => { counts[v] = (counts[v] || 0) + 1; }));

    // Stable order (by collection-wide count) so chips don't jump around as filters change
    const values = totals.value[group.key].filter(v => counts[v] || selected.value[group.key].includes(v));
    return {
      ...group,
      chips: values.map(value => ({
        value, count: counts[value] || 0, active: selected.value[group.key].includes(value),
      })),
    };
  }));

  const toggle = (groupKey, value) => {
    const picks = selected.value[groupKey];
    selected.value = {
      ...selected.value,
      [groupKey]: picks.includes(value) ? picks.filter(v => v !== value) : [...picks, value],
    };
  };

  const clearFilters = () => {
    selected.value = { moods: [], genres: [], decades: [] };
  };

  const deal = count => {
    dealt.value = shuffle(pool.value).slice(0, count);
  };

  const load = () => loadCollection();

  return {
    releases, isLoading, error, options, pool, dealt, toggle, clearFilters, deal, load,
  };
}
